import { useRef, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import ReCAPTCHA from 'react-google-recaptcha';
import {
  useValidateCaptchaV3TokenMutation,
  useValidateCaptchaV2TokenMutation,
} from '$store/modules/security';
import { errorHandler } from '$store/utils';
import { userLanguage } from '$translations/index';
import { ErrorMessageEn, ErrorMessageEs } from '$constants/alerts';

export function useCaptchaValidation() {
  const RECAPTCHA_MINIMUM_SCORE = 0.4;
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [validateCaptchaV3Token] = useValidateCaptchaV3TokenMutation();
  const [validateCaptchaV2Token] = useValidateCaptchaV2TokenMutation();
  const [showV2Captcha, setShowV2Captcha] = useState(false);
  const [v2CaptchaCompleted, setV2CaptchaCompleted] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const recaptchaV2Ref = useRef<ReCAPTCHA>(null);
  const v2SiteKey = import.meta.env.REACT_APP_RECAPTCHA_V2_SITE_KEY ?? '';

  const validateCaptcha = async (action: string): Promise<boolean> => {
    if (!executeRecaptcha) {
      errorHandler(
        '[ReCaptchaValidation]',
        userLanguage === 'es'
          ? ErrorMessageEs.RecaptchaUnavailable
          : ErrorMessageEn.RecaptchaUnavailable,
        new Error('reCaptcha not available'),
        true
      );
      return false;
    }

    setIsValidating(true);
    try {
      const reCaptchaV3Token = await executeRecaptcha(action);
      const validateV3Response = await validateCaptchaV3Token({
        Token: reCaptchaV3Token,
        Action: action,
      });

      if (
        'data' in validateV3Response &&
        validateV3Response.data.Valid &&
        validateV3Response.data.Score
      ) {
        const isValid =
          validateV3Response.data.Score >= RECAPTCHA_MINIMUM_SCORE;
        setIsValidating(false);
        return isValid;
      }

      // V3 failed, show V2 challenge
      setIsValidating(false);
      setShowV2Captcha(true);
      return false;
    } catch (error) {
      setIsValidating(false);
      setShowV2Captcha(true);
      return false;
    }
  };

  const validateV2Captcha = async (): Promise<boolean> => {
    if (!recaptchaV2Ref.current) {
      errorHandler(
        '[ReCaptchaValidation]',
        userLanguage === 'es'
          ? ErrorMessageEs.RecaptchaUnavailable
          : ErrorMessageEn.RecaptchaUnavailable,
        new Error('reCaptcha V2 not available'),
        true
      );
      return false;
    }

    const v2Token = recaptchaV2Ref.current.getValue();
    if (!v2Token) {
      errorHandler(
        '[ReCaptchaValidation]',
        userLanguage === 'es'
          ? ErrorMessageEs.CompleteV2Captcha
          : ErrorMessageEn.RecaptchaUnavailable,
        new Error('reCaptcha V2 token not available'),
        true
      );
      return false;
    }

    setIsValidating(true);
    try {
      const validateV2Response = await validateCaptchaV2Token({
        Token: v2Token,
      });

      if ('data' in validateV2Response && validateV2Response.data.Valid) {
        setIsValidating(false);
        setShowV2Captcha(false);
        setV2CaptchaCompleted(false);
        if (recaptchaV2Ref.current) {
          recaptchaV2Ref.current.reset();
        }
        return true;
      }

      setIsValidating(false);
      errorHandler(
        '[ReCaptchaValidation]',
        userLanguage === 'es'
          ? ErrorMessageEs.CaptchaValidationError
          : ErrorMessageEn.CaptchaValidationError,
        new Error('reCaptcha V2 validation failed'),
        true
      );
      if (recaptchaV2Ref.current) {
        recaptchaV2Ref.current.reset();
      }
      setV2CaptchaCompleted(false);
      return false;
    } catch (error) {
      setIsValidating(false);
      errorHandler(
        '[ReCaptchaValidation]',
        userLanguage === 'es'
          ? ErrorMessageEs.CaptchaValidationError
          : ErrorMessageEn.CaptchaValidationError,
        error as Error,
        true
      );
      if (recaptchaV2Ref.current) {
        recaptchaV2Ref.current.reset();
      }
      setV2CaptchaCompleted(false);
      return false;
    }
  };

  const resetV2Captcha = () => {
    setShowV2Captcha(false);
    setV2CaptchaCompleted(false);
    if (recaptchaV2Ref.current) {
      recaptchaV2Ref.current.reset();
    }
  };

  const onV2CaptchaChange = (value: string | null) => {
    setV2CaptchaCompleted(!!value);
  };

  return {
    validateCaptcha,
    validateV2Captcha,
    showV2Captcha,
    v2CaptchaCompleted,
    isValidating,
    recaptchaV2Ref,
    v2SiteKey,
    resetV2Captcha,
    onV2CaptchaChange,
  };
}

export default useCaptchaValidation;
